import * as React from "react";

// NPM
import { animated } from "react-spring";

// MUI
import {
	withStyles,
	WithStyles,
	createStyles,
	Theme,
} from "@material-ui/core/styles";
import { Typography, Tooltip, Grid, Button } from "@material-ui/core";

// NPM
import NumberFormat from "react-number-format";

// Utility
import Utility from "../domain/classes/Utility";

// Components
import StyledSectionContainer from "../components/common/SectionContainer.Component";
import AppointmentDetails from "../components/common/AppointmentDetails.Component";
import StyledVerifyVerificationCodeButton from "../components/common/VerifyVerificationCodeButton.Component";
import LoadingButton from "../components/common/LoadingButton.Component";
import { useApplicationManager } from "../contexts/ApplicationManager.Context";
import PermDeviceInformationIcon from '@material-ui/icons/PermDeviceInformation';
import EmailIcon from '@material-ui/icons/Email';
import AppointmentBookingRequest from "../domain/classes/AppointmentBookingRequest";
import { useCookies } from "react-cookie";
import GenericDialogV2 from "../components/common/GenericDialogV2.Component";
import {v4 as uuidv4} from 'uuid';

import {
	LAST_STAGE,
	SAD_COLOURED,
	ROOT_ID,
	QV_MEDICAL_PHONE_NUMBER
} from "../domain/utility/config";
import UnableToProcessBookingStageTwoError from "../domain/classes/errors/UnableToProcessBookingStageTwoError";
import Journey from "../domain/classes/Journey";
import JourneyStage from "../domain/enums/JourneyStage";

export const styles = (theme: Theme) =>
	createStyles({
		root: {},
		columnBtnGroup:{
			display:"flex",
			flexDirection: "column",
			gap: 5
		},
		assistBtn:{
			background:"#D54C4C",
			color: 'white'
		},
		verificationCodeInput: {
			width: "100%",
			padding: theme.spacing(2),
			fontSize: theme.spacing(3),
			textAlign: "center",
			fontFamily: "inherit",
		},
		imgContainer: { height: 80, marginBottom: theme.spacing(2) },
		img: { height: "100%", margin: "auto", display: "block" },
	});

export interface VerifyPasscodeSectionProps {}

type Channel = "sms" | "email" | null


const VerifyPasscodeSection = React.forwardRef<
	HTMLDivElement,
	VerifyPasscodeSectionProps & WithStyles<typeof styles>
>(({ classes }, ref) => {
	const [cookies, setCookies] = useCookies();
	const [verificationCode, setVerificationCode] = React.useState<string>("");
	const [retryAttempt, setRetryAttempt] = React.useState<number>(0);
	const [channel, setChannel] = React.useState<Channel>(null);
	const [loading, setLoading] = React.useState<boolean>(false);
	// Handler
	const handleVerificationCodeChange = (
		event: React.ChangeEvent<HTMLInputElement>
	) => {
		const val = event.target.value;
		setVerificationCode(val);
	};


	const {
		handleStageResponseChange,
		handleJourneyStageChange,
		patientDetails,
		journeyStage,
		stageResponse,
		clearApplicationData,
		clearCookies,
		pushDialog
	} = useApplicationManager();


	const getCodeFromSMS = async () => {
		setChannel("sms");
		await AppointmentBookingRequest.getVerificationCode(cookies["stage_id"], 0);
	}

	const getCodeFromEmail = async () => {
		setChannel("email");
		await AppointmentBookingRequest.getVerificationCode(cookies["stage_id"], 1);
	}

	const retrySendCode = () => {
		setChannel(null);
		setRetryAttempt(retryAttempt + 1)
	}

	const confirmAssistanceRequest = async () => {
		await AppointmentBookingRequest.sendAssistanceRequest(cookies["stage_id"]);
		clearApplicationData()
		clearCookies()
		handleJourneyStageChange(JourneyStage.AppointmentTypeSelection);

		pushDialog(
		<GenericDialogV2
		key={uuidv4()} 
		title="Request sent"
			actionStyle="Alert"
			content={
				<Typography variant="body1" component="p">
					Request is sent! Our staff will call you back as soon as possible. Thank you!
				</Typography>
			}
		/>
		)
	}

	const handleNextButtonClick = async () => {
		setLoading(true);
		const stageId = cookies["stage_id"];
		const receivedVia = channel?.toString() ?? "sms";
		try {
			try {
				const result = await AppointmentBookingRequest.startBookingStageTwo(
					{
						stageId,
						verificationCode,
						receivedVia
					}
				);

				// Utility.debug(result.data);

				setCookies("client_secret", result.data.clientSecret, {
					maxAge: 900,
				});

				handleStageResponseChange(result.data);

				if (result.data.stage === LAST_STAGE) {
					handleJourneyStageChange(JourneyStage.BookingConfirmed);
					return;
				}
			} catch (error) {
				throw new UnableToProcessBookingStageTwoError();
			}

			handleJourneyStageChange(Journey.goFrom(journeyStage, "forward"));
		} catch (error) {
			// Utility.debug(error.message);
			<GenericDialogV2 
				title="An error has occurred."
				actionStyle="Alert"
				content={
					<React.Fragment>
						<div className={classes.imgContainer}>
							<img
								src={SAD_COLOURED}
								className={classes.img}
								alt="Unable to process booking stage 2."
							/>
						</div>
						<Typography
							variant="body1"
							component="p"
							align="center"
						>
							{error}
						</Typography>
					</React.Fragment>
				}
				actions={
					<React.Fragment>
						<Button
							onClick={() => {
								window.open(`tel:${QV_MEDICAL_PHONE_NUMBER}`);
							}}
							color="primary"
							variant="contained"
						>
							Call
						</Button>
					</React.Fragment>
				}
			/>
			
		}

		Utility.scrollToTopOfElement(ROOT_ID);

		setLoading(false);
	};

	const sendAssistanceRequest = () => {
		pushDialog(
			<GenericDialogV2
			key={uuidv4()} 
			title="Request Staff Assistance"
				actionStyle="YesNo"
				onConfirm={confirmAssistanceRequest}
				content={
					<React.Fragment>
						<div className={classes.imgContainer}>
							<img
								src={SAD_COLOURED}
								className={classes.img}
								alt="Unable to process booking stage 1."
							/>
						</div>
						<Typography
							variant="body1"
							component="p"
							align="center"
						>
						{/* Sorry to know that you cannot complete verification. <br/>
						Would you like to send a notification to our friendly 
						staff regarding your booking request and ask to
						call you back at +{patientDetails.mobile}? */}
						It looks like something went wrong. Please confirm you would like a callback to help book an appointment via +{patientDetails.mobile}?
						</Typography>
					</React.Fragment>
				}
			/>
		)
	}

	return (
		<StyledSectionContainer
			ref={ref}
			className={classes.root}
			maxWidth="xs"
		>
			<Grid container spacing={2}>
				<Grid item xs={12}>
					<AppointmentDetails />
				</Grid>
				{
				channel == null &&
				<Grid item xs={12} className={classes.columnBtnGroup}>
					<Typography variant="body1" component="p">
						I would like to receive verification code via: 	
					</Typography>
					<Button fullWidth onClick={getCodeFromSMS} variant="outlined" color="primary" startIcon={<PermDeviceInformationIcon />}>
						+{patientDetails.mobile}
					</Button>
					{
						patientDetails.email && patientDetails.email.length > 0 &&  
						<Button fullWidth onClick={getCodeFromEmail} variant="outlined" color="primary" startIcon={<EmailIcon />}>
							{patientDetails.email}
						</Button>
					}
				</Grid>
				}

				{ channel !== null && 
				<React.Fragment>
					<Grid item xs={12}>
						<Typography variant="body1" component="p">
							Please enter the verification code that was sent to you
							to confirm your booking. The code will expire in 15
							minutes.
						</Typography>
					</Grid>
					<Grid item xs={12}>
						<Tooltip
							title="Please enter the verification code that was sent to your mobile number."
							arrow
							placement="top"
						>
							<NumberFormat
								format="######"
								className={classes.verificationCodeInput}
								mask="_"
								placeholder="Verification code"
								autoFocus
								onChange={handleVerificationCodeChange}
							/>
						</Tooltip>
					</Grid>
					<Grid container spacing={2} style={{ marginTop: 8 }}>
						<Grid item xs={12}>
							{/* <StyledVerifyVerificationCodeButton
								verificationCode={verificationCode}
							/> */}
							<div className={classes.columnBtnGroup}>
								<LoadingButton fullWidth isLoading={loading} onClick={handleNextButtonClick}>
									Next
								</LoadingButton>
								<Button fullWidth variant="outlined" onClick={retrySendCode}>
									I don't received code
								</Button>
								{ retryAttempt > 0 &&
									<Button classes={{root: classes.assistBtn}} fullWidth onClick={sendAssistanceRequest}>
										Need assistance
									</Button>	
								}
							</div>
						</Grid>
					</Grid>
					</React.Fragment> 
				}
			</Grid>					


		</StyledSectionContainer>
	);
});

const StyledVerifyPasscodeSection = withStyles(styles, {
	classNamePrefix: Utility.generateClassName("VerifyPasscodeSection"),
})(VerifyPasscodeSection);

export default StyledVerifyPasscodeSection;

export const AnimatedVerifyPasscodeSection = animated(
	StyledVerifyPasscodeSection
);